// Formato y cantidad, INLINE en la fila de controles del composer (v2).
//
// Antes los dos vivían adentro de la ruedita ⚙ (`ConfiguracionMenu`), o sea a
// dos clicks y sin poder ver los valores sin abrir nada. Acá el valor está
// siempre a la vista y la cantidad se cambia sin abrir nada
// (ADR-2026-09-08-formato-y-cantidad-inline-en-el-composer).
//
// Son controles de fila, no de menú: pills fantasma (sin fondo propio, fondo
// sólo al hover) para que no compitan con "+ Agregar" ni con el botón de
// enviar, que son las dos acciones de esa fila.

import React, { useEffect, useRef, useState } from 'react';
import { Check, Images, Minus, Plus } from 'lucide-react';
import { FORMAT_OPTIONS_V2 } from '../../mockData';
import { MAX_CANTIDAD, formatShortLabel } from './directMode';

// Pill fantasma compartido: alto de 32px (2px menos que los botones de 36 de
// la fila, para que se lean como secundarios) y fondo sólo al hover.
const PILL =
  'inline-flex items-center h-8 rounded-full text-[13px] text-[#02062BB3] transition-colors';

export interface FormatoInlineProps {
  formatSlug: string;
  onChangeFormat: (id: string) => void;
}

export const FormatoInline: React.FC<FormatoInlineProps> = ({
  formatSlug,
  onChangeFormat,
}) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Cierra con Escape. El click afuera lo cubre el scrim de abajo; Escape no,
  // y el popover tapa el textarea justo cuando estás por escribir.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const opt =
    FORMAT_OPTIONS_V2.find((f) => f.id === formatSlug) ?? FORMAT_OPTIONS_V2[0];

  return (
    <div className="relative shrink-0" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Formato: ${opt.name}`}
        title={opt.name}
        className={`${PILL} gap-1.5 px-2.5 ${
          open ? 'bg-neutral-100' : 'hover:bg-neutral-100'
        }`}
      >
        {/* Miniatura del ratio dibujada con un div: el formato se entiende por
            la FORMA antes que por el número. Ancho/alto fijos de 14px de caja
            y el rectángulo adentro con el aspect real. */}
        <RatioGlyph ratio={formatShortLabel(opt.id)} />
        <span className="font-medium tabular-nums">
          {formatShortLabel(opt.id)}
        </span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setOpen(false)} />
          {/* Un solo nivel: la lista completa se abre de una. En la ruedita
              esto era ⚙ → Formato → flyout. */}
          <div
            role="listbox"
            className="absolute bottom-full left-0 mb-2 w-[210px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-1.5 z-[56] flex flex-col gap-0.5"
          >
            {FORMAT_OPTIONS_V2.map((o) => {
              const active = o.id === formatSlug;
              return (
                <button
                  key={o.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChangeFormat(o.id);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 rounded-xl px-2.5 py-2 text-left transition-colors ${
                    active ? 'bg-[#1644EB0F]' : 'hover:bg-neutral-50'
                  }`}
                >
                  <RatioGlyph ratio={formatShortLabel(o.id)} />
                  <span className="text-[13.5px] text-[#02062BE6] truncate">
                    {o.name}
                  </span>
                  {active && (
                    <Check className="w-3.5 h-3.5 text-brand-primary shrink-0 ml-auto" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

/** Rectangulito con el aspect ratio real ("9:16" → alto y angosto). */
const RatioGlyph: React.FC<{ ratio: string }> = ({ ratio }) => {
  const [w, h] = ratio.split(':').map(Number);
  // Si el nombre no trae "a:b" (formato nuevo con otra copia), cae en cuadrado
  // en vez de romper el layout con un NaN.
  const ok = Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0;
  const escala = ok ? 13 / Math.max(w, h) : 13;
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center w-[13px] h-[13px] shrink-0"
    >
      <span
        className="block rounded-[2px] border-[1.5px] border-current"
        style={{
          width: ok ? w * escala : 11,
          height: ok ? h * escala : 11,
        }}
      />
    </span>
  );
};

export interface CantidadInlineProps {
  cantidad: number;
  onChangeCantidad: (n: number) => void;
}

export const CantidadInline: React.FC<CantidadInlineProps> = ({
  cantidad,
  onChangeCantidad,
}) => (
  // `group` para que el − y el + sólo se pinten cuando el mouse está encima del
  // control entero: quietos son dos glifos tenues al lado del número, no dos
  // botones más en una fila que ya tiene varios.
  <div
    className={`${PILL} group gap-0.5 px-1 hover:bg-neutral-100`}
    title="Cantidad de imágenes"
  >
    <Images
      className="w-[15px] h-[15px] mx-1 shrink-0 text-[#3C3C43]/45"
      strokeWidth={2}
      aria-hidden="true"
    />
    <button
      type="button"
      aria-label="Menos imágenes"
      disabled={cantidad <= 1}
      onClick={() => onChangeCantidad(Math.max(1, cantidad - 1))}
      className="inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-neutral-200 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
    >
      <Minus className="w-3.5 h-3.5" strokeWidth={2.5} />
    </button>
    <span
      className="w-4 text-center font-semibold text-[#02062BE6] tabular-nums"
      aria-live="polite"
    >
      {cantidad}
    </span>
    <button
      type="button"
      aria-label="Más imágenes"
      disabled={cantidad >= MAX_CANTIDAD}
      onClick={() => onChangeCantidad(Math.min(MAX_CANTIDAD, cantidad + 1))}
      className="inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-neutral-200 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
    >
      <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
    </button>
  </div>
);
